import { getSupabase } from './supabase';
import type {
  Customer,
  QuoteRequest,
  Quote,
  Appointment,
  Team,
  Job,
  Payment,
  Expense,
  AppNotification,
  Activity,
} from '@/types';

const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

const str = (v: unknown): string => (v == null ? '' : String(v));

interface SeedData {
  services: string[];
  regions: string[];
  teams: Team[];
  expenseCategories: string[];
}

// Seeds only configuration / reference data. Never operational or financial demo records.
export async function ensureConfigSeed(seed: SeedData): Promise<void> {
  const supabase = getSupabase();

  const { data: services } = await supabase.from('viaypii_services').select('id');
  if (!services || services.length === 0) {
    await supabase
      .from('viaypii_services')
      .insert(seed.services.map((name) => ({ name })));
  }

  const { data: regions } = await supabase.from('viaypii_regions').select('id');
  if (!regions || regions.length === 0) {
    await supabase
      .from('viaypii_regions')
      .insert(seed.regions.map((name) => ({ name })));
  }

  const { data: cats } = await supabase.from('viaypii_expense_categories').select('id');
  if (!cats || cats.length === 0) {
    await supabase
      .from('viaypii_expense_categories')
      .insert(seed.expenseCategories.map((name) => ({ name })));
  }

  const { data: teams } = await supabase.from('viaypii_teams').select('id');
  if (!teams || teams.length === 0) {
    for (const t of seed.teams) {
      await supabase.from('viaypii_teams').insert({
        id: t.id,
        name: t.name,
        status: 'Müsait',
        current_job: '',
        location: '',
        next_job_time: '',
      });
      for (const m of t.members) {
        await supabase.from('viaypii_team_members').insert({ team_id: t.id, name: m });
      }
    }
  }
}

export async function loadCustomers(): Promise<Customer[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_customers')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: str(r.id),
    name: str(r.name),
    phone: str(r.phone),
    region: str(r.region),
    address: str(r.address),
    status: str(r.status),
    lastService: str(r.last_service),
    totalJobs: num(r.total_jobs),
    lastContact: str(r.last_contact),
    source: str(r.source),
  }));
}

export async function loadRequests(): Promise<QuoteRequest[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_requests')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const rows = data ?? [];

  const { data: acts, error: actErr } = await supabase
    .from('viaypii_request_activities')
    .select('*')
    .order('created_at', { ascending: true });
  if (actErr) throw actErr;

  const activityMap = new Map<string, Activity[]>();
  for (const a of acts ?? []) {
    const list = activityMap.get(str(a.request_id)) ?? [];
    list.push({ time: str(a.time), text: str(a.text) });
    activityMap.set(str(a.request_id), list);
  }

  return rows.map((r) => ({
    id: str(r.id),
    customerName: str(r.customer_name),
    phone: str(r.phone),
    service: str(r.service),
    region: str(r.region),
    requestDate: str(r.request_date),
    videoStatus: (str(r.video_status) || 'Video Yok') as QuoteRequest['videoStatus'],
    status: str(r.status) as QuoteRequest['status'],
    source: str(r.source),
    responsible: str(r.responsible),
    lastContact: str(r.last_contact),
    priority: (str(r.priority) || 'Normal') as QuoteRequest['priority'],
    propertyType: str(r.property_type),
    area: str(r.area),
    customerNote: str(r.customer_note),
    addressNote: str(r.address_note),
    activities: activityMap.get(str(r.id)) ?? [],
  }));
}

export async function loadQuotes(): Promise<Quote[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_quotes')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: str(r.id),
    customerName: str(r.customer_name),
    service: str(r.service),
    location: str(r.location),
    date: str(r.date),
    amount: num(r.amount),
    status: str(r.status) as Quote['status'],
    revision: num(r.revision),
    scope: str(r.scope),
    operationalNote: str(r.operational_note),
    customerNote: str(r.customer_note),
  }));
}

export async function loadAppointments(): Promise<Appointment[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_appointments')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: str(r.id),
    customerName: str(r.customer_name),
    phone: str(r.phone),
    service: str(r.service),
    location: str(r.location),
    address: str(r.address),
    date: str(r.date),
    startTime: str(r.start_time),
    duration: str(r.duration),
    team: str(r.team_name),
    status: str(r.status),
    quoteAmount: num(r.quote_amount),
    quoteId: str(r.quote_id),
    customerNote: str(r.customer_note),
    internalNote: str(r.internal_note),
  }));
}

export async function loadTeams(): Promise<Team[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_teams')
    .select('*')
    .order('created_at', { ascending: true });
  if (error) throw error;
  const rows = data ?? [];

  const { data: members, error: mErr } = await supabase
    .from('viaypii_team_members')
    .select('*')
    .order('id', { ascending: true });
  if (mErr) throw mErr;

  const memberMap = new Map<string, string[]>();
  for (const m of members ?? []) {
    const list = memberMap.get(str(m.team_id)) ?? [];
    list.push(str(m.name));
    memberMap.set(str(m.team_id), list);
  }

  return rows.map((r) => ({
    id: str(r.id),
    name: str(r.name),
    members: memberMap.get(str(r.id)) ?? [],
    status: str(r.status),
    currentJob: str(r.current_job),
    location: str(r.location),
    nextJobTime: str(r.next_job_time),
  }));
}

export async function loadJobs(): Promise<Job[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_jobs')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  const rows = data ?? [];

  const { data: acts, error: aErr } = await supabase
    .from('viaypii_job_activities')
    .select('*')
    .order('created_at', { ascending: true });
  if (aErr) throw aErr;

  const { data: notes, error: nErr } = await supabase
    .from('viaypii_job_notes')
    .select('*')
    .order('created_at', { ascending: true });
  if (nErr) throw nErr;

  const timelineMap = new Map<string, Activity[]>();
  for (const a of acts ?? []) {
    const list = timelineMap.get(str(a.job_id)) ?? [];
    list.push({ time: str(a.time), text: str(a.text) });
    timelineMap.set(str(a.job_id), list);
  }

  const noteMap = new Map<string, string[]>();
  for (const n of notes ?? []) {
    const list = noteMap.get(str(n.job_id)) ?? [];
    list.push(str(n.note));
    noteMap.set(str(n.job_id), list);
  }

  return rows.map((r) => ({
    id: str(r.id),
    customerName: str(r.customer_name),
    phone: str(r.phone),
    service: str(r.service),
    location: str(r.location),
    address: str(r.address),
    team: str(r.team_name),
    date: str(r.date),
    time: str(r.time),
    status: str(r.status) as Job['status'],
    scope: str(r.scope),
    jobValue: num(r.job_value),
    quoteId: str(r.quote_id),
    timeline: timelineMap.get(str(r.id)) ?? [],
    notes: noteMap.get(str(r.id)) ?? [],
  }));
}

export async function loadPayments(): Promise<Payment[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_payments')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: str(r.id),
    customerName: str(r.customer_name),
    jobId: str(r.job_id),
    quoteId: str(r.quote_id),
    date: str(r.date),
    amount: num(r.amount),
    method: str(r.method) as Payment['method'],
    description: str(r.description),
    internalNote: str(r.internal_note),
  }));
}

export async function loadExpenses(): Promise<Expense[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_expenses')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: str(r.id),
    date: str(r.date),
    category: str(r.category),
    amount: num(r.amount),
    description: str(r.description),
    jobId: str(r.job_id),
    team: str(r.team_name),
  }));
}

export async function loadNotifications(): Promise<AppNotification[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('viaypii_notifications')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []).map((r) => ({
    id: str(r.id),
    type: str(r.type) as AppNotification['type'],
    title: str(r.title),
    description: str(r.description),
    time: str(r.time),
    read: Boolean(r.read),
    actionPath: str(r.action_path),
  }));
}

// ---------- Persist (write) helpers ----------

export async function persistCustomer(c: Customer): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_customers').upsert({
    id: c.id,
    name: c.name,
    phone: c.phone,
    region: c.region,
    address: c.address,
    status: c.status,
    source: c.source,
    last_service: c.lastService,
    total_jobs: c.totalJobs,
    last_contact: c.lastContact,
  });
  if (error) throw error;
}

export async function persistRequest(r: QuoteRequest): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_requests').upsert({
    id: r.id,
    customer_name: r.customerName,
    phone: r.phone,
    service: r.service,
    region: r.region,
    request_date: r.requestDate,
    video_status: r.videoStatus,
    status: r.status,
    source: r.source,
    responsible: r.responsible,
    last_contact: r.lastContact,
    priority: r.priority,
    property_type: r.propertyType,
    area: r.area,
    customer_note: r.customerNote,
    address_note: r.addressNote,
  });
  if (error) throw error;
}

export async function persistRequestActivity(requestId: string, a: Activity): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_request_activities').insert({
    request_id: requestId,
    time: a.time,
    text: a.text,
  });
  if (error) throw error;
}

export async function persistQuote(q: Quote): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_quotes').upsert({
    id: q.id,
    customer_name: q.customerName,
    service: q.service,
    location: q.location,
    date: q.date,
    amount: q.amount,
    status: q.status,
    revision: q.revision,
    scope: q.scope,
    operational_note: q.operationalNote,
    customer_note: q.customerNote,
  });
  if (error) throw error;
}

export async function persistAppointment(a: Appointment): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_appointments').upsert({
    id: a.id,
    customer_name: a.customerName,
    phone: a.phone,
    service: a.service,
    location: a.location,
    address: a.address,
    date: a.date,
    start_time: a.startTime,
    duration: a.duration,
    team_name: a.team,
    status: a.status,
    quote_id: a.quoteId || null,
    quote_amount: a.quoteAmount,
    customer_note: a.customerNote,
    internal_note: a.internalNote,
  });
  if (error) throw error;
}

export async function persistJob(j: Job): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_jobs').upsert({
    id: j.id,
    customer_name: j.customerName,
    phone: j.phone,
    service: j.service,
    location: j.location,
    address: j.address,
    team_name: j.team,
    date: j.date,
    time: j.time,
    status: j.status,
    scope: j.scope,
    job_value: j.jobValue,
    quote_id: j.quoteId || null,
  });
  if (error) throw error;
}

export async function persistJobActivity(jobId: string, a: Activity): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_job_activities').insert({
    job_id: jobId,
    time: a.time,
    text: a.text,
  });
  if (error) throw error;
}

export async function persistJobNote(jobId: string, note: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_job_notes').insert({
    job_id: jobId,
    note,
  });
  if (error) throw error;
}

export async function persistPayment(p: Payment): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_payments').upsert({
    id: p.id,
    customer_name: p.customerName,
    job_id: p.jobId || null,
    quote_id: p.quoteId || null,
    date: p.date,
    amount: p.amount,
    method: p.method,
    description: p.description,
    internal_note: p.internalNote,
  });
  if (error) throw error;
}

export async function persistExpense(e: Expense): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_expenses').upsert({
    id: e.id,
    date: e.date,
    category: e.category,
    amount: e.amount,
    description: e.description,
    job_id: e.jobId || null,
    team_name: e.team,
  });
  if (error) throw error;
}

export async function persistNotification(n: AppNotification): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_notifications').upsert({
    id: n.id,
    type: n.type,
    title: n.title,
    description: n.description,
    time: n.time,
    read: n.read,
    action_path: n.actionPath,
  });
  if (error) throw error;
}

export async function persistNotificationRead(id: string, read: boolean): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_notifications').update({ read }).eq('id', id);
  if (error) throw error;
}

export async function persistTeam(t: Team): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase.from('viaypii_teams').upsert({
    id: t.id,
    name: t.name,
    status: t.status,
    current_job: t.currentJob,
    location: t.location,
    next_job_time: t.nextJobTime,
  });
  if (error) throw error;
}